'use client';

import * as React from 'react';
import { Accordion as BaseAccordion } from '@base-ui/react/accordion';
import styles from './Accordion.module.css';
import clsx from 'clsx';

export interface AccordionProps {
  children: React.ReactNode;
  type?: 'single' | 'multiple';
  defaultValue?: string[];
  className?: string;
}

export interface AccordionItemProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export interface AccordionTriggerProps {
  children: React.ReactNode;
  className?: string;
}

export interface AccordionContentProps {
  children: React.ReactNode;
  className?: string;
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  ({ children, type = 'single', defaultValue, className }, ref) => {
    return (
      <BaseAccordion.Root
        ref={ref}
        defaultValue={defaultValue}
        className={clsx(styles.accordion, className)}
      >
        {children}
      </BaseAccordion.Root>
    );
  }
);

Accordion.displayName = 'Accordion';

export const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ value, children, disabled, className, style }, ref) => {
    return (
      <BaseAccordion.Item
        ref={ref}
        value={value}
        disabled={disabled}
        className={clsx(styles.item, className)}
        style={style}
      >
        {children}
      </BaseAccordion.Item>
    );
  }
);

AccordionItem.displayName = 'AccordionItem';

export const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className }, ref) => {
    return (
      <BaseAccordion.Header className={styles.header}>
        <BaseAccordion.Trigger ref={ref} className={clsx(styles.trigger, className)}>
          {children}
          <svg
            className={styles.chevron}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </BaseAccordion.Trigger>
      </BaseAccordion.Header>
    );
  }
);

AccordionTrigger.displayName = 'AccordionTrigger';

export const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className }, ref) => {
    return (
      <BaseAccordion.Panel ref={ref} className={clsx(styles.panel, className)}>
        <div className={styles.content}>{children}</div>
      </BaseAccordion.Panel>
    );
  }
);

AccordionContent.displayName = 'AccordionContent';
